<script setup lang="ts">
export type BackgroundVideoSource = {
  src: string;
  type: string;
  media?: string;
};

type Props = {
  backgroundImage?: string;
  backgroundVideoSources?: BackgroundVideoSource[];
  allowBackgroundSound?: boolean;
  heading?: string;
  description?: string;
  textPosition?: "left" | "right";
  buttonText?: string;
  linkPath?: string;
};

const {
  backgroundImage = "",
  backgroundVideoSources = [],
  allowBackgroundSound = false,
  heading = "",
  description = "",
  textPosition = "left",
  buttonText = "",
  linkPath = "",
} = defineProps<Props>();
</script>

<template>
  <div class="site-viewport-container">
    <div
      class="fifty-fifty-block"
      :class="`fifty-fifty-block--${textPosition}`"
    >
      <div
        class="fifty-fifty-block__background"
        :class="`fifty-fifty-block__background--${textPosition}`"
      >
        <NuxtImg
          v-if="backgroundImage && backgroundVideoSources.length === 0"
          provider="imgix"
          preset="squareBannerImage"
          loading="lazy"
          :src="backgroundImage"
          sizes="sm:100vw md:50vw lg:50vw xl:50vw 2xl:50vw 3xl:50vw"
          alt="static image"
          class="fifty-fifty-block__background__media fifty-fifty-block__background__media--image"
          @error="setBrokenMediaFallback"
        />
        <BaseVideoPlayer
          v-else-if="backgroundVideoSources.length > 0"
          :muted="!allowBackgroundSound"
          class="fifty-fifty-block__background__media fifty-fifty-block__background__media--video"
          :poster="backgroundImage ? backgroundImage : ''"
        >
          <source
            v-for="(videoSource, index) in backgroundVideoSources"
            :key="index"
            :src="videoSource.src"
            :type="videoSource.type"
            :media="videoSource.media"
          />
        </BaseVideoPlayer>
      </div>
      <div class="fifty-fifty-block__content--wrapper">
        <div
          class="fifty-fifty-block__content"
          :class="`fifty-fifty-block__content--${textPosition}`"
        >
          <slot name="content">
            <h2
              v-if="heading"
              class="fifty-fifty-block__content--heading uppercase"
            >
              {{ heading }}
            </h2>
            <p
              v-if="description"
              class="fifty-fifty-block__content--description"
            >
              {{ description }}
            </p>
            <div class="fifty-fifty-block__content--button">
              <slot name="button">
                <BaseNuxtLinkButton
                  v-if="buttonText && linkPath"
                  :link-path="linkPath"
                >
                  {{ buttonText }}
                </BaseNuxtLinkButton>
              </slot>
            </div>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.fifty-fifty-block {
  @apply flex justify-center items-center flex-col text-center relative w-full;
  @apply bg-platform-bg-1 sm:aspect-square md:aspect-[2/1] md:text-left;

  &--left {
    @apply md:items-start;
  }

  &--right {
    @apply md:items-end;
  }

  &__background {
    @apply relative aspect-square w-full h-full bg-platform-bg-1 md:absolute md:w-1/2;

    &--left {
      @apply right-0;
    }

    &--right {
      @apply left-0;
    }

    &__media {
      @apply h-full object-cover w-full;
    }
  }

  &__content {
    @apply relative py-8 w-11/12 bg-platform-bg-1 sm:bg-transparent md:w-4/5;

    &--wrapper {
      @apply w-full md:w-1/2 flex items-center justify-center;
    }

    &--heading {
      @apply pb-4 text-4xl;
      @apply tracking-wide;
    }

    &--description {
      @apply text-base pb-4;
      @apply tracking-wide;
    }

    &--button {
      @apply pt-4;
    }
  }
}
</style>
